import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, Camera, FileText, Heart, MapPin } from "lucide-react";

function score(user) {
  if (!user) return { value: 0, items: [] };
  const photos = user.photos?.length || 0;
  const bio = (user.bio || "").length;
  const interests = user.interests?.length || 0;
  const items = [
    { key: "photos", label: "Add 3+ photos", icon: Camera, done: photos >= 3,
      progress: Math.min(1, photos / 3), weight: 30, hint: `${photos}/3 photos` },
    { key: "bio", label: "Bio of 80+ chars", icon: FileText, done: bio >= 80,
      progress: Math.min(1, bio / 80), weight: 20, hint: `${bio} chars` },
    { key: "interests", label: "Pick 4+ interests", icon: Heart, done: interests >= 4,
      progress: Math.min(1, interests / 4), weight: 15, hint: `${interests}/4 interests` },
    { key: "location", label: "Add a city", icon: MapPin, done: !!user.location,
      progress: user.location ? 1 : 0, weight: 10 },
    { key: "verified", label: "Verify your identity", icon: ShieldCheck, done: !!user.verified,
      progress: user.verified ? 1 : 0, weight: 25, hint: user.verified ? "Verified" : "3× more matches" },
  ];
  const value = Math.round(items.reduce((s, it) => s + it.progress * it.weight, 0));
  return { value, items };
}

export default function ProfileStrength({ user, compact = false }) {
  const { value, items } = score(user);
  const tone = value >= 85 ? "Strong" : value >= 60 ? "Solid" : value >= 30 ? "Sparse" : "Getting started";
  const color = value >= 85 ? "#4caf50" : value >= 60 ? "#913831" : "#D4A373";

  return (
    <div className={`rounded-3xl ${compact ? "p-4" : "p-6"} bg-white border border-[#E5DFD3]`}
         data-testid="profile-strength">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#737373]">Profile strength</div>
          <div className="font-serif text-3xl mt-1 leading-none text-[#1A1818]">
            {value}<span className="text-[#737373] text-lg">/100</span>
          </div>
          <div className="text-xs mt-1" style={{ color }} data-testid="profile-strength-tone">{tone}</div>
        </div>
        <Sparkles size={20} className="text-[#913831]" strokeWidth={1.25} />
      </div>
      <div className="mt-4 h-2 rounded-full bg-[#F0EBE1] overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700"
             style={{ width: `${value}%`, background: color }}
             data-testid="profile-strength-bar" />
      </div>

      <ul className="mt-5 space-y-2.5">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <li key={it.key} className="flex items-center gap-3 text-sm" data-testid={`ps-item-${it.key}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                it.done ? "bg-[#913831] text-white" : "bg-[#F0EBE1] text-[#737373]"
              }`}>
                <Icon size={12} strokeWidth={2} />
              </span>
              <span className={`flex-1 ${it.done ? "text-[#737373] line-through" : "text-[#1A1818]"}`}>
                {it.label}
              </span>
              {it.hint && <span className="text-xs text-[#737373]">{it.hint}</span>}
            </li>
          );
        })}
      </ul>

      {value < 100 && !compact && (
        <Link to={user?.verified ? "/settings" : "/verify"}
          data-testid="profile-strength-cta"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full py-2.5 text-sm font-medium">
          <Sparkles size={13} /> {user?.verified ? "Improve your profile" : "Verify next"}
        </Link>
      )}
    </div>
  );
}
